/**
 * ProactiveAIChat - AI Chat with Proactive Suggestions
 * 
 * This component enhances the AI chat experience with:
 * - Context-aware proactive suggestions based on patient data
 * - Voice command integration
 * - Smart follow-up questions
 * - Action shortcuts (schedule visit, log symptom, etc.)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  Mic,
  MicOff,
  Send,
  Loader2,
  Sparkles,
  Brain,
  Clock,
  AlertCircle,
  Calendar,
  Pill,
  Phone,
  FileText,
  Heart,
  Activity,
  ChevronRight,
  Volume2,
  VolumeX,
  RefreshCw,
  Wand2,
  Lightbulb,
  MessageCircle,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SpeechDictation from "./SpeechDictation";
import { cleanClinicalResponse } from "@/utils/aiUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Proactive suggestion categories
const SUGGESTION_CATEGORIES = {
  clinical: {
    icon: Activity,
    color: "bg-blue-500",
    label: "Clinical",
  },
  medication: {
    icon: Pill,
    color: "bg-purple-500",
    label: "Medication",
  },
  communication: {
    icon: MessageCircle,
    color: "bg-green-500",
    label: "Communication",
  },
  scheduling: {
    icon: Calendar,
    color: "bg-orange-500",
    label: "Scheduling",
  },
  documentation: {
    icon: FileText,
    color: "bg-teal-500",
    label: "Documentation",
  },
  alert: {
    icon: AlertCircle,
    color: "bg-red-500",
    label: "Alert",
  },
};

// Quick action suggestions based on context
const QUICK_ACTIONS = [
  { id: "pain_check", label: "Check pain level", category: "clinical", prompt: "What is the patient's current pain level and any changes since last visit?" },
  { id: "med_review", label: "Review medications", category: "medication", prompt: "Review current medications and check for any PRN usage in the past 24 hours" },
  { id: "family_update", label: "Update family", category: "communication", prompt: "Prepare a family update summary about the patient's current condition" },
  { id: "schedule_visit", label: "Schedule visit", category: "scheduling", prompt: "What visits need to be scheduled for this patient in the next week?" },
  { id: "symptom_trend", label: "Symptom trends", category: "clinical", prompt: "Show me the symptom trends for this patient over the past 2 weeks" },
  { id: "care_plan", label: "Care plan review", category: "documentation", prompt: "Review the current care plan and identify any needed updates" },
  { id: "prn_usage", label: "PRN medication use", category: "medication", prompt: "Summarize PRN medication usage patterns for comfort care" },
  { id: "idg_prep", label: "IDG preparation", category: "documentation", prompt: "Help me prepare for the next IDG meeting with a patient summary" },
];

// Proactive suggestion templates
const PROACTIVE_TEMPLATES = [
  {
    trigger: "pain_increase",
    message: "I noticed the patient's pain level has increased. Would you like me to help document this or suggest interventions?",
    actions: ["Document pain assessment", "Review pain medication", "Contact physician"],
  },
  {
    trigger: "visit_due",
    message: "A scheduled visit is due soon. Would you like me to prepare documentation or review the care plan?",
    actions: ["Start visit note", "Review last visit", "Check pending orders"],
  },
  {
    trigger: "medication_change",
    message: "There was a recent medication change. Should I summarize the changes for the family or update the MAR?",
    actions: ["Family summary", "Update MAR", "Check interactions"],
  },
  {
    trigger: "symptom_alert",
    message: "New symptoms have been reported. Would you like guidance on assessment or documentation?",
    actions: ["Symptom assessment", "Document findings", "Escalation guidance"],
  },
];

export default function ProactiveAIChat({
  patientId,
  patientName,
  patientContext,
  onActionTriggered,
  compact = false,
}) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const speechSynthRef = useRef(null);

  // Generate proactive suggestions based on patient context
  const generateSuggestions = useCallback(async () => {
    if (!patientContext) {
      // Default suggestions without patient context
      setSuggestions(QUICK_ACTIONS.slice(0, 4));
      return;
    }

    try {
      // Generate context-aware suggestions
      const contextSuggestions = [];

      // Check for pain-related suggestions
      if (patientContext.last_pain_score > 5) {
        contextSuggestions.push({
          id: "pain_intervention",
          label: `Pain level is ${patientContext.last_pain_score}/10 - Review interventions`,
          category: "alert",
          prompt: `The patient's pain level is ${patientContext.last_pain_score}/10. What interventions have been tried and what additional options should we consider?`,
          priority: "high",
        });
      }

      // Check for visit scheduling
      if (patientContext.days_since_last_visit > 5) {
        contextSuggestions.push({
          id: "visit_overdue",
          label: `${patientContext.days_since_last_visit} days since last visit`,
          category: "scheduling",
          prompt: `It's been ${patientContext.days_since_last_visit} days since the last visit. Help me schedule an appropriate follow-up.`,
          priority: "medium",
        });
      }

      // Check for medication PRN usage
      if (patientContext.prn_medications_used > 0) {
        contextSuggestions.push({
          id: "prn_review",
          label: `${patientContext.prn_medications_used} PRN medications used today`,
          category: "medication",
          prompt: `The patient has used ${patientContext.prn_medications_used} PRN medications today. Should we review the effectiveness and consider any adjustments?`,
          priority: "medium",
        });
      }

      // Check for upcoming IDG
      if (patientContext.next_idg_date) {
        const daysToIdg = Math.ceil(
          (new Date(patientContext.next_idg_date) - new Date()) / (1000 * 60 * 60 * 24)
        );
        if (daysToIdg <= 3) {
          contextSuggestions.push({
            id: "idg_prep",
            label: `IDG meeting in ${daysToIdg} days - Prepare summary`,
            category: "documentation",
            prompt: `The IDG meeting is in ${daysToIdg} days. Help me prepare a comprehensive patient summary including current status, goals, and any concerns.`,
            priority: "high",
          });
        }
      }

      // Add default quick actions if we don't have enough context suggestions
      const remaining = 4 - contextSuggestions.length;
      if (remaining > 0) {
        contextSuggestions.push(...QUICK_ACTIONS.slice(0, remaining));
      }

      setSuggestions(contextSuggestions);
    } catch (err) {
      console.error("Error generating suggestions:", err);
      setSuggestions(QUICK_ACTIONS.slice(0, 4));
    }
  }, [patientContext]);

  useEffect(() => {
    generateSuggestions();
  }, [generateSuggestions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle sending a message
  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: messageText,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // Send to AI chat endpoint
      const response = await axios.post(`${API_URL}/api/chat/contextual`, {
        message: messageText,
        patient_id: patientId,
        patient_context: patientContext,
        conversation_history: messages.slice(-5),
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: cleanClinicalResponse(response.data.response || response.data.message),
        timestamp: new Date().toISOString(),
        suggestions: response.data.follow_up_suggestions,
        actions: response.data.suggested_actions,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Update suggestions based on AI response
      if (response.data.follow_up_suggestions?.length > 0) {
        setSuggestions(
          response.data.follow_up_suggestions.map((s, idx) => ({
            id: `followup_${idx}`,
            label: s,
            category: "clinical",
            prompt: s,
          }))
        );
      }

      // Text-to-speech
      if (ttsEnabled && response.data.response) {
        speakText(response.data.response);
      }
    } catch (err) {
      console.error("Chat error:", err);
      
      // Fallback response
      const fallbackMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "I'm here to help. Could you provide more details about your question?",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, fallbackMessage]);
      
      toast.error("AI service temporarily unavailable. Basic chat mode active.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.prompt);
    handleSendMessage(suggestion.prompt);
  };

  // Handle voice transcript
  const handleVoiceTranscript = (transcript) => {
    setInputValue((prev) => (prev ? prev + " " + transcript : transcript));
  };

  // Text-to-speech
  const speakText = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      speechSynthRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Stop speaking
  const stopSpeaking = () => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  };

  return (
    <Card className={`flex flex-col ${compact ? "h-[400px]" : "h-[600px]"}`}>
      <CardHeader className="pb-2 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            AI Clinical Assistant
            {patientName && (
              <Badge variant="outline" className="ml-2">
                {patientName}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTtsEnabled(!ttsEnabled)}
              className="h-8 w-8 p-0"
            >
              {ttsEnabled ? (
                <Volume2 className="w-4 h-4 text-primary" />
              ) : (
                <VolumeX className="w-4 h-4 text-muted-foreground" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="h-8 w-8 p-0"
            >
              <Lightbulb
                className={`w-4 h-4 ${showSuggestions ? "text-yellow-500" : "text-muted-foreground"}`}
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={generateSuggestions}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Proactive Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="px-3 py-2 border-b bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center gap-1 mb-2 text-xs text-muted-foreground">
            <Sparkles className="w-3 h-3" />
            <span>Suggestions</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.slice(0, 4).map((suggestion) => {
              const category = SUGGESTION_CATEGORIES[suggestion.category] || SUGGESTION_CATEGORIES.clinical;
              const Icon = category.icon;
              return (
                <Button
                  key={suggestion.id}
                  variant="outline"
                  size="sm"
                  className={`h-7 text-xs gap-1.5 ${
                    suggestion.priority === "high" ? "border-red-300 bg-red-50" : ""
                  }`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  data-testid={`suggestion-${suggestion.id}`}
                >
                  <Icon className="w-3 h-3" />
                  {suggestion.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <Brain className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground text-sm">
                Ask me anything about patient care, symptoms, medications, or documentation.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                I can help with clinical decisions, care planning, and documentation.
              </p>
            </div>
          )}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {message.role === "assistant" && (
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    <Brain className="w-4 h-4" />
                  </AvatarFallback>
                </Avatar>
              )}
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-slate-100 dark:bg-slate-800"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                {message.actions && message.actions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {message.actions.map((action, idx) => (
                      <Button
                        key={idx}
                        variant="secondary"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => {
                          if (onActionTriggered) {
                            onActionTriggered(action);
                          }
                        }}
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        {action}
                      </Button>
                    ))}
                  </div>
                )}
                <p className="text-xs opacity-50 mt-1">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </p>
              </div>
              {message.role === "user" && (
                <Avatar className="w-8 h-8">
                  <AvatarFallback>
                    <MessageCircle className="w-4 h-4" />
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-3">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  <Brain className="w-4 h-4" />
                </AvatarFallback>
              </Avatar>
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          className="flex items-center gap-2"
        >
          <SpeechDictation
            onTranscript={handleVoiceTranscript}
            buttonVariant="outline"
            buttonSize="icon"
            className="h-10 w-10"
          />
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask about patient care, symptoms, medications..."
            className="flex-1"
            disabled={isLoading}
            data-testid="chat-input"
          />
          <Button type="submit" disabled={isLoading || !inputValue.trim()} data-testid="chat-send">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    </Card>
  );
}
